import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IconChevronLeft } from "@tabler/icons-react";
import { StepProgress } from "../../components/ui/StepProgress";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import { useMeasuredHeight } from "../../hooks/use-measured-height";
import { Button } from "../../components/ui/Button";
import Loader from "../../components/Loader";
import { IntroStep } from "./steps/IntroStep";
import { CountryStep } from "./steps/CountryStep";
import { NameStep } from "./steps/NameStep";
import { OtpStep } from "./steps/OtpStep";
import { ProfessionStep } from "./steps/ProfessionStep";
import { SpecialtyStep } from "./steps/SpecialtyStep";
import { PracticeStep } from "./steps/PracticeStep";
import { PracticeAgentPanel } from "./steps/PracticeAgentPanel";
import { RegistryStep } from "./steps/RegistryStep";
import { PartnerObjectionContent } from "../../components/partners/PartnerObjectionContent";
import {
  PartnerObjectionSheets,
  useObjectionSheets,
} from "../../components/partners/PartnerObjectionSheets";
import { StepFooter } from "./components/StepFooter";
import { OnboardingStepActions } from "./components/OnboardingStepActions";
import { OnboardingOAuthModal } from "./components/OnboardingOAuthModal";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { updateOnboardingFlow } from "../../domains/auth/stores/onboardingFlowStore";
import { cn } from "../../lib/utils";
import {
  CAROUSEL_SLIDES,
  OTP_SLIDE,
  INTRO_SLIDE,
  NAME_SLIDE,
  PARTNER_OBJECTION_FORM_ID,
  useOnboardingFlow,
  type OnboardingFlow,
} from "./useOnboardingFlow";
import { DesktopOnboardingPage } from "./desktop/DesktopOnboardingPage";

// Signal native iOS to disable scroll when keyboard opens (prevents content
// jumping). Also handle scrolling focused inputs into view since native scroll
// is disabled. Mobile/native-only concern — desktop web never needs it.
function useNativeKeyboardScrollManagement(): void {
  useEffect(() => {
    document.documentElement.classList.add("keyboard-managed");

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLTextAreaElement) &&
        !(target instanceof HTMLSelectElement)
      ) {
        return;
      }

      setTimeout(() => {
        const parentTarget = target.closest("[data-scroll-target]");
        if (parentTarget instanceof HTMLElement) {
          // Select/combobox: scroll so the trigger stays visible at the top
          parentTarget.scrollIntoView({ block: "start", behavior: "smooth" });
        } else {
          // Regular input: scroll with bottom margin so it sits above the CTA
          target.style.scrollMarginBottom = "300px";
          target.scrollIntoView({ block: "end", behavior: "smooth" });
          setTimeout(() => {
            target.style.scrollMarginBottom = "";
          }, 600);
        }
      }, 500);
    };
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.documentElement.classList.remove("keyboard-managed");
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);
}

// ---------------------------------------------------------------------------
// Main Page — owns the flow (single source of truth) and dispatches to the
// platform layout. See README.md for the flow architecture.
// ---------------------------------------------------------------------------

export default function OnboardingPage() {
  const flow = useOnboardingFlow();

  if (flow.showFullScreenLoader) {
    return <Loader expanded />;
  }

  return (
    <>
      {isDesktopLayout() ? (
        <DesktopOnboardingPage flow={flow} />
      ) : (
        <MobileOnboardingPage flow={flow} />
      )}
      <OnboardingOAuthModal flow={flow} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Mobile / native layout — carousel with overlaid frosted header/footer bands
// the step content scrolls behind. The intro slide opts out of both bands and
// carries its own chrome.
// ---------------------------------------------------------------------------

function MobileOnboardingPage({ flow }: { flow: OnboardingFlow }) {
  const { t } = useTranslation();
  useNativeKeyboardScrollManagement();

  // Overlaid header/footer bands measured live so the scrolling step content
  // can be padded to exactly clear them (and scroll behind them, visible through
  // the backdrop-blur).
  const headerOverlay = useMeasuredHeight();
  const footerOverlay = useMeasuredHeight();
  const objectionSheets = useObjectionSheets();

  const step = flow.step;

  // Insets that keep the scrolling step content clear of the overlaid bands.
  // Width is set inline (no Tailwind 1/7 fraction) so the N-slide carousel math
  // stays driven by CAROUSEL_SLIDES.
  const slideWidth = `${100 / CAROUSEL_SLIDES}%`;
  const slideStyle = {
    width: slideWidth,
    paddingTop: headerOverlay.height,
    paddingBottom: footerOverlay.height,
  };
  // The intro slide renders neither band, so it takes no inset at all. Measured
  // heights are NOT usable here: `useMeasuredHeight` keeps its last value when
  // the node unmounts, so stepping back onto the intro would inherit a phantom
  // footer inset.
  const introSlideStyle = { width: slideWidth };

  return (
    <KeyboardAvoidingView
      behavior="height"
      className="absolute inset-0 flex flex-col bg-ds-white md:mx-auto md:w-full md:max-w-lg"
    >
      <div className="relative flex-1 min-h-0">
        {/* ---- Scrolling step content (passes behind the overlaid bands) ---- */}
        {/* Every slide is `relative`: an absolute descendant with no positioned
            ancestor inside its slide (the `sr-only` checkbox of the partner
            objection) escapes the slide's clip and makes this wrapper taller
            than the screen once the keyboard shrinks it. The focus handler's
            `scrollIntoView` then scrolls this wrapper, and every slide slides
            up under the header. */}
        <div className="absolute inset-0 overflow-hidden">
          {/* While the account is created in place, the slide it runs on stays
              drawn but takes no answer: a row tapped under the spinner would
              change what is being written. */}
          <fieldset
            disabled={flow.isFinalizing}
            className="absolute inset-y-0 left-0 m-0 flex min-w-0 border-0 p-0 transition-transform duration-400 ease-out"
            style={{
              width: `${CAROUSEL_SLIDES * 100}%`,
              transform: `translateX(-${step * (100 / CAROUSEL_SLIDES)}%)`,
            }}
          >
            {/* Slide 0: Email code — the FIRST slide an email signup answers. In the
                DOM for everyone (the translateX math is fixed by CAROUSEL_SLIDES)
                and walked only by a fresh email signup, like the RPPS and
                Practice slides. */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              <OtpStep
                code={flow.otpCode}
                error={
                  flow.otpState.status === "awaiting_code"
                    ? flow.otpState.error
                    : null
                }
                isBusy={
                  flow.otpState.status === "sending" ||
                  flow.otpState.status === "verifying"
                }
                cooldownRemaining={flow.otpCooldownRemaining}
                onCodeChange={flow.setOtpCode}
                onSubmit={flow.handleOtpSubmit}
                onResend={flow.handleOtpResend}
                isActive={step === OTP_SLIDE}
              />
            </div>

            {/* Slide 1: Intro — what Juisci is + the healthcare-professional
                  gate. Renders its own header/footer, so no band insets. */}
            <div
              className="relative shrink-0 overflow-y-auto px-6"
              data-scroll-container
              style={introSlideStyle}
            >
              {/* `env()` directly, never `var(--safe-area-top)`: the token form
                    leaves the title under the iOS notch, while the direct one is
                    what every other notch-clearing header in the app (including
                    the one below) ships with. The inset is ADDED to the design
                    spacing, not `max()`ed with it: on a notched iPhone the inset
                    alone (59px) wins the max and parks the headline flush under
                    the status bar. Off-notch (`env()` = 0) this is still 32px. */}
              <div
                className="flex min-h-full flex-col"
                style={{
                  paddingTop: "calc(env(safe-area-inset-top) + 32px)",
                  paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)",
                }}
              >
                <IntroStep
                  onAccept={flow.handleIntroAccept}
                  onDeclineHcp={flow.handleIntroDeclineHcp}
                />
              </div>
            </div>

            {/* Slide 2: Name */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              <NameStep
                firstName={flow.firstName}
                lastName={flow.lastName}
                onFirstNameChange={(v) => {
                  updateOnboardingFlow({ firstName: v });
                }}
                onLastNameChange={(v) => {
                  updateOnboardingFlow({ lastName: v });
                }}
                onNext={flow.handleNameNext}
                onSignupStarted={flow.handleSignupStarted}
                hasSignupStarted={flow.hasSignupStarted}
                isActive={step === NAME_SLIDE}
                // The carousel is one question per slide: the country keeps
                // its own.
                countryField={null}
                variant="mobile"
              />
            </div>

            {/* Slide 3: Country */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              <form
                id="onboarding-form-country"
                className="flex flex-col w-full"
                onSubmit={(e) => {
                  e.preventDefault();
                  flow.handleCountrySubmit();
                }}
              >
                <CountryStep
                  country={flow.country}
                  searchQuery={flow.countrySearch}
                  onCountryChange={(v) => {
                    flow.changeCountry(v);
                  }}
                />
              </form>
            </div>

            {/* Slide 4: RPPS lookup (visited only by French healthcare
                  professionals, otherwise jumped over). */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              {flow.registryState !== null && (
                <RegistryStep
                  stpsSearch={flow.registry === "stps" ? flow.stpsSearch : null}
                  state={flow.registryState}
                  registry={flow.registry}
                  registryId={flow.registryId}
                  registryInitials={flow.registryInitials}
                  onRegistryIdChange={flow.setRegistryId}
                  onRegistryInitialsChange={flow.setRegistryInitials}
                  onSelectCandidate={flow.handleRegistrySelectCandidate}
                  onLoadMore={flow.loadMoreRegistryCandidates}
                  repsFirstName={flow.repsFirstName}
                  repsFirstSurname={flow.repsFirstSurname}
                  repsSecondSurname={flow.repsSecondSurname}
                  repsResultsCurrent={flow.repsResultsCurrent}
                  onRepsFirstNameChange={flow.setRepsFirstName}
                  onRepsFirstSurnameChange={flow.setRepsFirstSurname}
                  onRepsSecondSurnameChange={flow.setRepsSecondSurname}
                />
              )}
            </div>

            {/* Slide 5: Profession */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              {flow.hasProfessionStep && (
                <ProfessionStep
                  professions={flow.professions}
                  selected={flow.selectedProfession}
                  searchQuery={flow.professionSearch}
                  professionList={flow.professionList}
                  onShowMore={flow.showMoreProfessions}
                  onSelect={(v) => {
                    flow.selectProfession(v);
                  }}
                  onNext={flow.handleProfessionNext}
                />
              )}
            </div>

            {/* Slide 6: Specialty */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              {flow.selectedProfession !== null && flow.hasSpecialtyStep && (
                <SpecialtyStep
                  specialties={flow.specialtyRows.rows}
                  selectedSpecialty={flow.effectiveSpecialty}
                  isAssisting={flow.specialtyRows.isAssisting}
                  onSelectSpecialty={(v) => {
                    flow.selectSpecialty(v);
                  }}
                  onSubmit={flow.handleSpecialtySubmit}
                />
              )}
            </div>

            {/* Slide 7: Practice Profiles. Rendered only when the declared
                Specialty has a catalogue — the slide stays in the DOM for the
                fixed translateX math, so its CONTENT is what is gated, exactly
                as the specialty slide's is. */}
            <div
              className="relative flex shrink-0 flex-col overflow-hidden px-5"
              data-scroll-container
              style={slideStyle}
            >
              {/* Three STATES of this slide, not three slides: same title, same
                  footer band, only the panel differs — so the carousel's
                  transition into here is the only movement the practitioner
                  sees. The agent reads, then reads back what it found, and the
                  catalogue is what "Edit" opens. */}
              {flow.practiceStatus === "inferring" ? (
                <PracticeAgentPanel
                  status="reading"
                  firstName={flow.firstName}
                  specialtyLabel={flow.effectiveSpecialtyLabel}
                  variant="mobile"
                />
              ) : flow.practiceStatus === "recap" ? (
                <PracticeAgentPanel
                  status="recap"
                  names={flow.practiceRecapNames}
                  onEdit={flow.handlePracticeEdit}
                  onSubmit={flow.handlePracticeSubmit}
                />
              ) : (
                flow.practiceOffer !== null &&
                flow.hasPracticeStep && (
                  <PracticeStep
                    offer={flow.practiceOffer}
                    selected={flow.practiceSelection}
                    checkedOnOpen={flow.practiceCheckedOnOpen}
                    disambiguation={flow.practiceDisambiguation}
                    specialtyLabel={flow.effectiveSpecialtyLabel}
                    phase={flow.practicePhase}
                    declaration={flow.practiceDeclarationOffer}
                    matching={flow.practiceAssist.matching}
                    isAssisting={flow.practiceAssist.isAssisting}
                    onToggle={flow.handlePracticeToggle}
                    onToggleDisambiguation={
                      flow.handlePracticeToggleDisambiguation
                    }
                    onSetPractices={flow.handlePracticeSet}
                    onPhaseChange={flow.setPracticePhase}
                    onChangeSpecialty={flow.handleChangeSpecialty}
                    onSubmit={flow.handlePracticeSubmit}
                    variant="mobile"
                  />
                )
              )}
            </div>

            {/* Slide 8: Partner objection (Figma O1), the last step. Its
                Continue saves the choice, with the box checked or not, then
                finalizes the account in place. */}
            <div
              className="relative shrink-0 overflow-y-auto px-5"
              data-scroll-container
              style={slideStyle}
            >
              <form
                id={PARTNER_OBJECTION_FORM_ID}
                // 32px above and below the content (Figma 4770:2043).
                className="flex w-full flex-col py-8"
                onSubmit={(e) => {
                  e.preventDefault();
                  void flow.handlePartnerObjectionSubmit();
                }}
              >
                <PartnerObjectionContent
                  layout="mobile"
                  objected={flow.partnerObjected}
                  onObjectedChange={(objected) => {
                    updateOnboardingFlow({ partnerObjected: objected });
                  }}
                  onOpenPartners={() => {
                    objectionSheets.open("partners");
                  }}
                  onOpenPrivacy={() => {
                    objectionSheets.open("privacy");
                  }}
                />
              </form>
            </div>
          </fieldset>
        </div>

        {/* ---- Header overlay (steps + title/subtitle) ---- */}
        {/* The intro slide carries no chrome at all (no back — its own CTAs are
            the only way forward, and there is no progress to report yet). The
            other carousel steps keep the full frosted chrome. */}
        <div
          ref={headerOverlay.ref}
          className={cn(
            "absolute inset-x-0 top-0 z-20",
            step !== INTRO_SLIDE && "bg-ds-white/70 backdrop-blur-md"
          )}
        >
          {step === INTRO_SLIDE ? null : (
            <>
              <div className="flex items-center justify-between px-6 pt-[max(env(safe-area-inset-top),20px)] pb-4 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={flow.handleBack}
                  className={cn(
                    "p-0 text-black size-7 shrink-0",
                    // Hide back only while the account is being created. On
                    // the first slide of a run it leaves onboarding.
                    flow.isFinalizing
                      ? "invisible pointer-events-none"
                      : "visible"
                  )}
                >
                  <IconChevronLeft className="size-full!" />
                </Button>
                {/* Starts right after the back button (Figma 4827:8653). */}
                <StepProgress
                  totalSteps={flow.totalSteps}
                  currentStep={flow.progressStep}
                  className="flex-1 min-w-0 justify-start px-8"
                  variant="mobile"
                />
                {/* Mounted on every slide so the bar keeps its width when the
                    practice slide shows it. As tall as Back, to be as easy to
                    tap. */}
                <button
                  type="button"
                  onClick={flow.handlePracticeSkip}
                  className={cn(
                    "h-7 shrink-0 cursor-pointer whitespace-nowrap border-none bg-transparent p-0 font-dm-sans text-sm font-medium text-ds-slate-600",
                    flow.showsPracticeSkip
                      ? "visible"
                      : "invisible pointer-events-none"
                  )}
                >
                  {t("Later")}
                </button>
              </div>

              {flow.stepHeader !== null && (
                <div className="flex flex-col gap-2 px-5 pt-3 pb-5">
                  <h1 className="font-dm-sans text-heading-display font-semibold text-[#2c2c2c]">
                    {flow.stepHeader.title}
                  </h1>
                  {flow.stepHeader.subtitle !== null && (
                    <p className="font-dm-sans text-ui-body text-[#929292]">
                      {flow.stepHeader.subtitle}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <PartnerObjectionSheets
          layout="mobile"
          openSheet={objectionSheets.openSheet}
          onClose={objectionSheets.close}
        />

        {/* ---- Footer overlay (CTAs) ---- */}
        {flow.showStepFooter && (
          <div
            ref={footerOverlay.ref}
            className="absolute inset-x-0 bottom-0 z-20 bg-ds-white/70 backdrop-blur-sm"
          >
            <StepFooter>
              <OnboardingStepActions flow={flow} variant="mobile" />
            </StepFooter>
          </div>
        )}
      </div>
    </KeyboardAvoidingView>
  );
}
